import React, { useEffect, useState, useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { T } from "services/lang/LanguageService";
import { Button } from "primereact/button";
import { MultiSelect } from "primereact/multiselect";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { DataTable } from "primereact/datatable";
import { Tag } from "primereact/tag";
import { COLUMN_OPTIONS } from "components/basic/datasets-properties/DatasetsPropertiesTable";
import { Chips } from "primereact/chips";
import userSelectionsStore from "components/basic/datasets-properties/selectedColumnsStore";
import CopyTextWithButton from "components/basic/copy-text-with-button/CopyTextWithButton";
import { useGetDatasetInfo, useGetDatasetsInfoByTags } from "api/api-hooks/datasets-hooks";
import { TabPanel, TabView } from "primereact/tabview";
import DatasetCategoryDropdown from "components/basic/dropdowns/dataset-category/DatasetCategoryDropdown";
import { DatasetInfo as DatasetInfoModel } from "api/model/dataset/DatasetInfo";
import { copyToClipBoard } from "./utils";
import SingleDatasetInfo from "./SingleDatasetInfo";
import DatasetIDSearchField from "./DatasetIDSearchField";
import { exportToCSV } from "../weather-monitoring/WeatherMonitoringUtils";

interface DatasetInfoSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

const DatasetInfoSearch = ({ isOpen, onClose }: DatasetInfoSearchProps) => {
  const [displayModal, setDisplayModal] = useState(isOpen);
  const [id, setId] = useState<string | null>(null);
  const [idNumber, setIdNumber] = useState<number | undefined>();
  const [selectedType, setSelectedType] = useState<string>("REMOTE_CATEGORIZED_DATASET");
  const typesOfDataset: Array<string> = ["CATEGORIZED_DATASET", "REMOTE_CATEGORIZED_DATASET"];
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [defaultCategory, setDefaultCategory] = useState<number>(1);
  const [tags, setTags] = useState<string[]>([]);
  const [selectedDataset, setSelectedDataset] = useState<DatasetInfoModel | null>(null);
  const selectedColumns = userSelectionsStore(state => state.userSelections.selectedColumns);
  const setSelectedColumns = userSelectionsStore(state => state.setUserSelections);

  const { data, isLoading, isSuccess, isFetching } = useGetDatasetInfo(id, { enabled: !!id });
  const { data: dataByTags, isLoading: isLoadingByTags } = useGetDatasetsInfoByTags(
    defaultCategory,
    tags,
    {
      enabled: tags.length > 0,
    }
  );

  useEffect(() => {
    setDisplayModal(isOpen);
  }, [isOpen]);

  useEffect(() => {
    const fullID: string = `${idNumber?.toString()}:${selectedType}`;
    if (displayModal && idNumber != null) {
      setId(fullID);
    }
  }, [idNumber, selectedType]);

  const onHide = () => {
    setDisplayModal(false);
    onClose();
    setId(null);
    setIsCopied(false);
    setIdNumber(undefined);
    setSelectedDataset(null);
    setTags([]);
    setDefaultCategory(1);
    setActiveIndex(0);
  };

  const onCopy = () => {
    if (isSuccess && data?.title !== undefined) {
      copyToClipBoard(data.title as string);
      setIsCopied(true);
    }
  };

  const renderFooter = () => {
    return (
      <div className="mt-4">
        <Button
          label={T("action.close")}
          icon="pi pi-times"
          onClick={() => onHide()}
          className="component-editor-button icon-red"
        />
        {activeIndex === 0 && (
          <Button
            label={isCopied ? T("label.copied") : T("action.copy")}
            icon="pi pi-copy"
            onClick={() => onCopy()}
            autoFocus
            className="component-editor-button icon-green"
            disabled={!isSuccess || isLoading || data?.title === null}
          />
        )}
      </div>
    );
  };

  const handleSetTags = (value: string[]) => {
    setTags(value);
    if (value.length === 0) {
      setSelectedDataset(null);
    }
  };

  const formattedData = useMemo(
    () =>
      dataByTags
        ? dataByTags.map(e => ({ ...e, id: e.identifier ? e.identifier.split(":") : [] }))
        : [],
    [dataByTags]
  );

  const idBody = (rowData: DatasetInfoModel) => {
    return <CopyTextWithButton text={rowData.identifier!} copyableText={rowData.identifier!} />;
  };

  const titleBody = (rowData: DatasetInfoModel) => {
    return <CopyTextWithButton text={rowData.title!} copyableText={rowData.title!} />;
  };

  const groupBody = (rowData: DatasetInfoModel) => {
    if (rowData.groupTitle && rowData.groupId) {
      return `${rowData.groupTitle} [${rowData.groupId}]`;
    }
    if (rowData.groupTitle) {
      return rowData.groupTitle;
    }
    if (rowData.groupId) {
      return `[${rowData.groupId}]`;
    }
    return "";
  };

  const categoryBody = (rowData: DatasetInfoModel) => {
    if (rowData.categoryTitle && rowData.categoryId) {
      return `${rowData.categoryTitle} [${rowData.categoryId}]`;
    }
    if (rowData.categoryTitle) {
      return rowData.categoryTitle;
    }
    if (rowData.categoryId) {
      return `[${rowData.categoryId}]`;
    }
    return "";
  };

  const datasetClassBody = (rowData: DatasetInfoModel) => {
    return <Tag value={rowData.datasetClass} />;
  };

  const tagsBody = (rowData: DatasetInfoModel) => {
    return (
      <div className="flex gap-1 flex-wrap">
        {rowData.tags.map((e, index) => (
          <Tag value={e} key={index} />
        ))}
      </div>
    );
  };

  const bodyMapper = {
    id: idBody,
    title: titleBody,
    groupId: groupBody,
    categoryId: categoryBody,
    datasetClass: datasetClassBody,
    tags: tagsBody,
  };

  const formattedDataForExport = useMemo(() => {
    return formattedData.map(row => {
      const formattedRow: { [key: string]: any } = {};
      selectedColumns.forEach(column => {
        formattedRow[column] = row[column as keyof DatasetInfoModel];
      });
      return formattedRow;
    });
  }, [formattedData, selectedColumns]);

  return (
    <Dialog
      header={T("label.dataset-information-search")}
      visible={displayModal}
      style={{ width: activeIndex === 0 ? "60vw" : "70vw" }}
      footer={renderFooter()}
      onHide={() => onHide()}
      blockScroll
    >
      <TabView activeIndex={activeIndex} onTabChange={e => setActiveIndex(e.index)}>
        <TabPanel header={T("action.search-by-id")}>
          <DatasetIDSearchField
            idNumber={idNumber}
            selectedType={selectedType}
            typesOfDataset={typesOfDataset}
            setIdNumber={setIdNumber}
            setSelectedType={setSelectedType}
          />
          {data && (
            <SingleDatasetInfo
              dataToUse={data}
              isLoading={isLoading}
              isSuccess={isSuccess}
              isFetching={isFetching}
              idNumber={idNumber}
            />
          )}
        </TabPanel>
        <TabPanel header={T("action.search-by-tags")}>
          <div className="flex flex-column gap-2">
            <div className="flex flex-row justify-content-between">
              <div className="flex flex-row gap-2">
                <div className="flex flex-column">
                  <label htmlFor="defaultCategory">{T("action.select-dataset-category")}</label>
                  <DatasetCategoryDropdown
                    value={defaultCategory}
                    onChange={e => setDefaultCategory(e)}
                    className="mt-2 h-2"
                  />
                </div>
                <p className="font-semibold text-2xl mt-5 mb-4 ml-3 mr-3">:</p>
                <div className="flex flex-column">
                  <label htmlFor="tags">{T("word.tags")}</label>
                  <Chips
                    className="mt-2 h-2"
                    placeholder={T("word.tags")}
                    value={tags}
                    onChange={e => handleSetTags(e.value)}
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-1 justify-content-end">
                <Button
                  label="CSV"
                  className="component-editor-button icon-blue mt-4"
                  icon="pi pi-file"
                  onClick={() => exportToCSV(formattedDataForExport, "dataset-properties")}
                />
                <MultiSelect
                  value={selectedColumns}
                  options={COLUMN_OPTIONS}
                  onChange={e => setSelectedColumns(e.value)}
                  maxSelectedLabels={2}
                  style={{ height: "36px", marginTop: "15px" }}
                />
              </div>
            </div>
            {tags.length > 0 && dataByTags && dataByTags?.length === 0 && (
              <div>{T("message.dataset-info-search.no-datasets-found-with-the-selected-tags-in-the-selected-category")}</div>
            )}
            {tags.length > 0 && dataByTags && dataByTags?.length > 0 && (
              <div className="pb-2">{T("message.dataset-info-search.there-are-datasets-with-the-selected-tags-in-the-selected-category", {dataByTags: dataByTags?.length})}</div>
            )}
            {isLoadingByTags && <ProgressSpinner />}
          </div>
          <div className="flex flex-column gap-1 relative">
            <DataTable value={formattedData} loading={isLoading} size="small">
              {selectedColumns.map(column => {
                const header = COLUMN_OPTIONS.find(e => e.value === column)?.label;
                return (
                  <Column
                    key={column}
                    header={header}
                    field={column}
                    body={bodyMapper[column as keyof typeof bodyMapper]}
                  />
                );
              })}
            </DataTable>
          </div>
          {selectedDataset && <SingleDatasetInfo dataToUse={selectedDataset} />}
        </TabPanel>
      </TabView>
    </Dialog>
  );
};

export default DatasetInfoSearch;
